import { api } from './client.js';
import type {
  AcceptBookingPayload,
  AdjustCreditsPayload,
  AdminDashboard,
  AppSettingsDto,
  AssignNutritionPlanPayload,
  AssignProgramPayload,
  AuditLogPage,
  AuditLogQuery,
  AvailabilityQuery,
  AvailabilityResponse,
  AvailabilityExceptionDto,
  BackgroundJobPage,
  BookingRequestDto,
  BookingRequestPage,
  BookingRequestQuery,
  CalendarQuery,
  CalendarResponse,
  CancelBookingPayload,
  CheckInDto,
  CheckInPage,
  CheckInQuery,
  CheckInReviewContext,
  CheckInTemplateDto,
  ClientDashboard,
  ClientAnalysisOverview,
  ClientDirectoryQuery,
  ClientDirectoryRow,
  ClientGoal,
  ClientHealthProfile,
  ClientNote,
  ClientPackageDto,
  ClientPackagePage,
  ClientProfile,
  ClientTimelineEntry,
  ClientWorkspaceOverview,
  CoachAvailabilityRuleDto,
  CoachProfileSummary,
  CompleteProgressPhotoUploadPayload,
  CompleteSessionPayload,
  ConsentRecord,
  ContentReadinessReport,
  ConversationDto,
  ConversationPage,
  ConversationQuery,
  ConvertLeadPayload,
  ConvertLeadResponse,
  CreateBookingRequestPayload,
  CreateClientPackagePayload,
  CreateLeadPayload,
  CreateLeadResponse,
  CreateNutritionPlanPayload,
  CreateProgramPayload,
  CreateSessionPayload,
  CreditBalance,
  CreditLedgerPage,
  CreditLedgerQuery,
  DeclineBookingPayload,
  DocumentDto,
  DocumentPage,
  DocumentQuery,
  ExerciseDto,
  ExercisePage,
  ExerciseProgressionSeries,
  ExerciseQuery,
  ExternalCalendarConnectionDto,
  FeedbackDto,
  FeedbackPage,
  FeedbackQuery,
  FileAssetDto,
  FitnessTestDto,
  ForgotPasswordRequest,
  LeadPage,
  LeadQuery,
  LogSetPayload,
  LoginRequest,
  MeasurementDto,
  MeasurementPage,
  MailboxMessageDto,
  MailboxPage,
  MailStatusDto,
  MessagePage,
  MfaEnrolmentChallenge,
  ModelAssetContent,
  NotificationDeliveryPage,
  NotificationDeliveryQuery,
  NotificationPage,
  NotificationPreferenceDto,
  NotificationQuery,
  NutritionEstimate,
  NutritionEstimateRequest,
  NutritionLogPage,
  NutritionPlanDto,
  NutritionPlanPage,
  NutritionPlanQuery,
  NutritionTemplateDto,
  OnboardingDraft,
  OnboardingState,
  Paginated,
  PaymentDto,
  ProgramContent,
  ProgressOverview,
  ProgressPhotoDto,
  ProgressPhotoPage,
  ProgressPhotoUploadRequest,
  ProgressQuery,
  ProposeTimePayload,
  PublicResultPage,
  PublicResultQuery,
  RecordManualPaymentPayload,
  RecordMeasurementPayload,
  RegisterRequest,
  RegisterResponse,
  ReopenCheckInPayload,
  ReportDefinition,
  ReportFilters,
  ReportResult,
  RequestCheckInPayload,
  RequestSubstitutionPayload,
  RescheduleRequestPayload,
  ResetPasswordRequest,
  ReviewCheckInPayload,
  SaveCheckInDraftPayload,
  ServicePackageDto,
  SessionContext,
  SessionDto,
  SessionPage,
  SessionQuery,
  SessionTypeDto,
  SendMessagePayload,
  SendMailboxEmailPayload,
  SignedDownload,
  SiteContent,
  StartWorkoutPayload,
  SubmitCheckInPayload,
  SubmitFeedbackPayload,
  TestimonialPage,
  TrainingLocationDto,
  TrainingProgramDto,
  TrainingTemplateDto,
  UpdateAppSettingsPayload,
  UpdateContentPayload,
  UpdateFeedbackPayload,
  UpdateLeadPayload,
  UpdateMediaPayload,
  UpdateNotificationPreferencesPayload,
  UpdateNutritionDraftPayload,
  UpdateProgramDraftPayload,
  UpdateSessionPayload,
  UploadDocumentPayload,
  UploadTicket,
  UpsertCheckInTemplatePayload,
  UpsertExercisePayload,
  UpsertNutritionLogPayload,
  UpsertPublicResultPayload,
  UpsertServicePackagePayload,
  UpsertTestimonialPayload,
  UserSessionSummary,
  VerifyEmailRequest,
  WorkoutLogDto,
  WorkoutLogPage,
  WorkoutLogQuery,
} from '@ircf/contracts';

/**
 * Typed endpoint surface.
 *
 * One function per API operation, grouped by the four namespaces the platform
 * exposes: `/public`, `/me`, `/coach` (folded into admin, which is role-gated
 * server-side) and `/admin`. Components never construct a path or a query
 * string - that is the whole point of this module.
 */

// --- Authentication -----------------------------------------------------------

export const authApi = {
  /**
   * Resolves the current session. `allowUnauthenticated` prevents the initial
   * probe from triggering the global redirect for a genuinely anonymous visitor.
   */
  session: () => api.get<SessionContext>('/me/session', { allowUnauthenticated: true }),

  login: (payload: LoginRequest) => api.post<SessionContext>('/auth/login', payload),
  logout: () => api.post<void>('/auth/logout'),
  register: (payload: RegisterRequest) => api.post<RegisterResponse>('/auth/register', payload),
  verifyEmail: (payload: VerifyEmailRequest) => api.post<void>('/auth/verify-email', payload),
  resendVerification: (email: string) => api.post<void>('/auth/resend-verification', { email }),
  forgotPassword: (payload: ForgotPasswordRequest) => api.post<void>('/auth/forgot-password', payload),
  resetPassword: (payload: ResetPasswordRequest) => api.post<void>('/auth/reset-password', payload),
  changePassword: (payload: { currentPassword: string; newPassword: string }) =>
    api.post<void>('/auth/change-password', payload),

  devices: () => api.get<UserSessionSummary[]>('/auth/sessions'),
  revokeDevice: (id: string) => api.delete<void>(`/auth/sessions/${id}`),
  revokeAllOtherDevices: () => api.post<void>('/auth/sessions/revoke-others'),

  startMfaEnrolment: () => api.post<MfaEnrolmentChallenge>('/auth/mfa/enrol'),
  confirmMfaEnrolment: (totpCode: string) =>
    api.post<SessionContext>('/auth/mfa/enrol/confirm', { totpCode }),
  disableMfa: (password: string) => api.post<SessionContext>('/auth/mfa/disable', { password }),
} as const;

// --- Public -------------------------------------------------------------------

export const publicApi = {
  site: () => api.get<SiteContent>('/public/site'),
  programs: () => api.get<ProgramContent[]>('/public/programs'),
  packages: () => api.get<ServicePackageDto[]>('/public/packages'),
  sessionTypes: () => api.get<SessionTypeDto[]>('/public/session-types'),
  locations: () => api.get<TrainingLocationDto[]>('/public/locations'),
  results: (query?: PublicResultQuery) => api.get<PublicResultPage>('/public/results', { query }),
  faqs: () => api.get<SiteContent['faqs']>('/public/faqs'),
  availability: (query: AvailabilityQuery) =>
    api.get<AvailabilityResponse>('/public/availability', { query }),
  createLead: (payload: CreateLeadPayload) =>
    api.post<CreateLeadResponse>('/public/leads', payload),
} as const;

// --- Client (`/me`) -----------------------------------------------------------

export const meApi = {
  profile: () => api.get<ClientProfile>('/me'),
  updateProfile: (payload: Partial<ClientProfile>) => api.patch<ClientProfile>('/me', payload),
  dashboard: () => api.get<ClientDashboard>('/me/dashboard'),

  onboarding: () => api.get<OnboardingState>('/me/onboarding'),
  saveOnboarding: (draft: Partial<OnboardingDraft>) =>
    api.put<OnboardingState>('/me/onboarding', draft),
  completeOnboarding: () => api.post<OnboardingState>('/me/onboarding/complete'),

  consents: () => api.get<ConsentRecord[]>('/me/consents'),
  grantConsent: (type: string, documentVersion: string | null) =>
    api.post<ConsentRecord>('/me/consents', { type, documentVersion }),
  revokeConsent: (type: string, reason: string) =>
    api.post<ConsentRecord>('/me/consents/revoke', { type, reason }),

  goals: () => api.get<ClientGoal[]>('/me/goals'),

  sessionTypes: () => api.get<SessionTypeDto[]>('/me/session-types'),
  availability: (query: AvailabilityQuery) =>
    api.get<AvailabilityResponse>('/me/availability', { query }),

  bookingRequests: (query?: BookingRequestQuery) =>
    api.get<BookingRequestPage>('/me/booking-requests', { query }),
  createBookingRequest: (payload: CreateBookingRequestPayload) =>
    api.post<BookingRequestDto>('/me/booking-requests', payload),
  cancelBookingRequest: (id: string, payload: CancelBookingPayload) =>
    api.post<BookingRequestDto>(`/me/booking-requests/${id}/cancel`, payload),
  requestReschedule: (id: string, payload: RescheduleRequestPayload) =>
    api.post<BookingRequestDto>(`/me/booking-requests/${id}/reschedule`, payload),
  respondToProposal: (id: string, alternativeId: string, accepted: boolean) =>
    api.post<BookingRequestDto>(`/me/booking-requests/${id}/respond`, { alternativeId, accepted }),

  sessions: (query?: SessionQuery) => api.get<SessionPage>('/me/sessions', { query }),
  session: (id: string) => api.get<SessionDto>(`/me/sessions/${id}`),
  cancelSession: (id: string, payload: CancelBookingPayload) =>
    api.post<SessionDto>(`/me/sessions/${id}/cancel`, payload),
  calendar: (query: CalendarQuery) => api.get<CalendarResponse>('/me/calendar', { query }),
  /** Returns raw `text/calendar`, so it bypasses envelope unwrapping. */
  sessionIcs: (id: string) =>
    api.get<string>(`/me/sessions/${id}/ics`, { responseType: 'text' }),
  calendarIcs: () => api.get<string>('/me/calendar.ics', { responseType: 'text' }),

  trainingPrograms: () => api.get<TrainingProgramDto[]>('/me/training-programs'),
  trainingProgram: (id: string) => api.get<TrainingProgramDto>(`/me/training-programs/${id}`),

  workouts: (query?: WorkoutLogQuery) => api.get<WorkoutLogPage>('/me/workouts', { query }),
  workout: (id: string) => api.get<WorkoutLogDto>(`/me/workouts/${id}`),
  startWorkout: (payload: StartWorkoutPayload) => api.post<WorkoutLogDto>('/me/workouts', payload),
  updateWorkout: (id: string, payload: Record<string, unknown>) =>
    api.patch<WorkoutLogDto>(`/me/workouts/${id}`, payload),
  logSet: (workoutId: string, payload: LogSetPayload) =>
    api.post<WorkoutLogDto>(`/me/workouts/${workoutId}/sets`, payload),
  reportExerciseOutcome: (workoutId: string, payload: Record<string, unknown>) =>
    api.post<WorkoutLogDto>(`/me/workouts/${workoutId}/exercise-outcome`, payload),
  requestSubstitution: (payload: RequestSubstitutionPayload) =>
    api.post<void>('/me/exercise-substitutions', payload),

  nutritionPlans: () => api.get<NutritionPlanDto[]>('/me/nutrition-plans'),
  nutritionPlan: (id: string) => api.get<NutritionPlanDto>(`/me/nutrition-plans/${id}`),
  nutritionLogs: (query?: { from?: string; to?: string }) =>
    api.get<NutritionLogPage>('/me/nutrition-logs', { query }),
  upsertNutritionLog: (payload: UpsertNutritionLogPayload) =>
    api.post<void>('/me/nutrition-logs', payload),

  checkIns: (query?: CheckInQuery) => api.get<CheckInPage>('/me/check-ins', { query }),
  checkIn: (id: string) => api.get<CheckInDto>(`/me/check-ins/${id}`),
  saveCheckInDraft: (id: string, payload: SaveCheckInDraftPayload) =>
    api.put<CheckInDto>(`/me/check-ins/${id}`, payload),
  submitCheckIn: (id: string, payload: SubmitCheckInPayload) =>
    api.post<CheckInDto>(`/me/check-ins/${id}/submit`, payload),

  forms: () => api.get<import('@ircf/contracts').FormAssignmentDto[]>('/me/forms'),
  form: (id: string) => api.get<import('@ircf/contracts').FormAssignmentDto>(`/me/forms/${id}`),
  saveForm: (id: string, payload: import('@ircf/contracts').SubmitFormPayload) =>
    api.put<import('@ircf/contracts').FormAssignmentDto>(`/me/forms/${id}`, payload),
  submitForm: (id: string, payload: import('@ircf/contracts').SubmitFormPayload) =>
    api.post<import('@ircf/contracts').FormAssignmentDto>(`/me/forms/${id}/submit`, payload),

  progress: (query?: ProgressQuery) => api.get<ProgressOverview>('/me/progress', { query }),
  analysis: (query?: ProgressQuery) => api.get<ClientAnalysisOverview>('/me/analysis', { query }),
  exerciseProgression: (exerciseId: string) =>
    api.get<ExerciseProgressionSeries>(`/me/progress/exercises/${exerciseId}`),
  measurements: (query?: { from?: string; to?: string; page?: number }) =>
    api.get<MeasurementPage>('/me/measurements', { query }),
  recordMeasurement: (payload: RecordMeasurementPayload) =>
    api.post<MeasurementDto>('/me/measurements', payload),

  progressPhotos: (query?: { from?: string; to?: string; page?: number }) =>
    api.get<ProgressPhotoPage>('/me/progress-photos', { query }),
  requestProgressPhotoUpload: (payload: ProgressPhotoUploadRequest) =>
    api.post<UploadTicket>('/me/progress-photos/upload-url', payload),
  completeProgressPhotoUpload: (payload: CompleteProgressPhotoUploadPayload) =>
    api.post<ProgressPhotoDto>('/me/progress-photos/complete', payload),
  progressPhotoDownloadUrl: (id: string) =>
    api.post<SignedDownload>(`/me/progress-photos/${id}/download-url`),
  deleteProgressPhoto: (id: string) => api.delete<void>(`/me/progress-photos/${id}`),

  feedback: (query?: FeedbackQuery) => api.get<FeedbackPage>('/me/feedback', { query }),
  submitFeedback: (payload: SubmitFeedbackPayload) =>
    api.post<FeedbackDto>('/me/feedback', payload),
  submitSessionFeedback: (sessionId: string, payload: SubmitFeedbackPayload) =>
    api.post<FeedbackDto>(`/me/sessions/${sessionId}/feedback`, payload),

  conversations: (query?: ConversationQuery) =>
    api.get<ConversationPage>('/me/conversations', { query }),
  messages: (conversationId: string, query?: { page?: number; pageSize?: number }) =>
    api.get<MessagePage>(`/me/conversations/${conversationId}/messages`, { query }),
  sendMessage: (payload: SendMessagePayload) => api.post<ConversationDto>('/me/messages', payload),
  markConversationRead: (conversationId: string, upToMessageId: string) =>
    api.post<void>(`/me/conversations/${conversationId}/read`, { upToMessageId }),
  requestMessageAttachmentUpload: (payload: {
    fileName: string;
    contentType: string;
    contentLength: number;
  }) => api.post<UploadTicket>('/me/messages/attachment-url', payload),
  attachmentDownloadUrl: (attachmentId: string) =>
    api.post<SignedDownload>(`/me/message-attachments/${attachmentId}/download-url`),

  documents: (query?: DocumentQuery) => api.get<DocumentPage>('/me/documents', { query }),
  documentDownloadUrl: (id: string) => api.post<SignedDownload>(`/me/documents/${id}/download-url`),
  acknowledgeDocument: (id: string, statement: string | null) =>
    api.post<DocumentDto>(`/me/documents/${id}/acknowledge`, { statement }),

  notifications: (query?: NotificationQuery) =>
    api.get<NotificationPage>('/me/notifications', { query }),
  markNotificationRead: (id: string) => api.post<void>(`/me/notifications/${id}/read`),
  markAllNotificationsRead: () => api.post<void>('/me/notifications/read-all'),
  unreadCounts: () => api.get<import('@ircf/contracts').UnreadCounts>('/me/unread-counts'),
  notificationPreferences: () =>
    api.get<NotificationPreferenceDto[]>('/me/notification-preferences'),
  updateNotificationPreferences: (payload: UpdateNotificationPreferencesPayload) =>
    api.patch<NotificationPreferenceDto[]>('/me/notification-preferences', payload),
  pushSubscriptionStatus: () =>
    api.get<import('@ircf/contracts').PushSubscriptionStatus>('/me/push-subscriptions'),
  registerPushSubscription: (payload: import('@ircf/contracts').RegisterPushSubscriptionPayload) =>
    api.post<import('@ircf/contracts').PushSubscriptionDto>('/me/push-subscriptions', payload),
  removePushSubscription: (endpoint?: string) =>
    api.delete<void>('/me/push-subscriptions', {
      query: endpoint ? { endpoint } : undefined,
    }),

  packages: () => api.get<ClientPackageDto[]>('/me/packages'),
  creditBalance: () => api.get<CreditBalance>('/me/credits'),
  creditLedger: (query?: CreditLedgerQuery) => api.get<CreditLedgerPage>('/me/credits/ledger', { query }),
} as const;

// --- Admin and coach ----------------------------------------------------------

export const adminApi = {
  dashboard: () => api.get<AdminDashboard>('/admin/dashboard'),

  administrators: () => api.get<AdministratorAccount[]>('/admin/administrators'),
  inviteAdministrator: (payload: InviteAdministratorPayload) =>
    api.post<AdministratorAccount>('/admin/administrators', payload),

  calendar: (query: CalendarQuery) => api.get<CalendarResponse>('/admin/calendar', { query }),
  calendarIcs: (query?: { coachId?: string }) =>
    api.get<string>('/admin/calendar.ics', { query, responseType: 'text' }),

  bookingRequests: (query?: BookingRequestQuery) =>
    api.get<BookingRequestPage>('/admin/booking-requests', { query }),
  bookingRequest: (id: string) => api.get<BookingRequestDto>(`/admin/booking-requests/${id}`),
  acceptBookingRequest: (id: string, payload: AcceptBookingPayload) =>
    api.post<{ request: BookingRequestDto; session: SessionDto }>(
      `/admin/booking-requests/${id}/accept`,
      payload,
    ),
  declineBookingRequest: (id: string, payload: DeclineBookingPayload) =>
    api.post<BookingRequestDto>(`/admin/booking-requests/${id}/decline`, payload),
  proposeTime: (id: string, payload: ProposeTimePayload) =>
    api.post<BookingRequestDto>(`/admin/booking-requests/${id}/propose-time`, payload),
  updateBookingNotes: (
    id: string,
    payload: { internalNote?: string | null; clientVisibleNote?: string | null },
  ) => api.patch<BookingRequestDto>(`/admin/booking-requests/${id}`, payload),

  sessions: (query?: SessionQuery) => api.get<SessionPage>('/admin/sessions', { query }),
  session: (id: string) => api.get<SessionDto>(`/admin/sessions/${id}`),
  createSession: (payload: CreateSessionPayload) => api.post<SessionDto>('/admin/sessions', payload),
  updateSession: (id: string, payload: UpdateSessionPayload) =>
    api.patch<SessionDto>(`/admin/sessions/${id}`, payload),
  completeSession: (id: string, payload: CompleteSessionPayload) =>
    api.post<SessionDto>(`/admin/sessions/${id}/complete`, payload),
  markNoShow: (id: string, payload: { clientIds: string[]; note: string | null }) =>
    api.post<SessionDto>(`/admin/sessions/${id}/no-show`, payload),
  cancelSession: (id: string, payload: CancelBookingPayload & { returnCredits: boolean }) =>
    api.post<SessionDto>(`/admin/sessions/${id}/cancel`, payload),
  sendSessionReminder: (id: string) => api.post<void>(`/admin/sessions/${id}/remind`),

  clients: (query?: ClientDirectoryQuery) =>
    api.get<Paginated<ClientDirectoryRow>>('/admin/clients', { query }),
  client: (id: string) => api.get<ClientProfile>(`/admin/clients/${id}`),
  clientOverview: (id: string) =>
    api.get<ClientWorkspaceOverview>(`/admin/clients/${id}/overview`),
  updateClient: (id: string, payload: Partial<ClientProfile>) =>
    api.patch<ClientProfile>(`/admin/clients/${id}`, payload),
  createClient: (payload: {
    email: string;
    preferredName: string;
    fullName: string;
    phone: string;
    pathway: string;
    assignedCoachId: string | null;
    sendInvitation: boolean;
  }) => api.post<ClientProfile>('/admin/clients', payload),
  assignCoach: (id: string, coachId: string) =>
    api.post<ClientProfile>(`/admin/clients/${id}/assign-coach`, { coachId }),
  suspendClient: (id: string, reason: string) =>
    api.post<ClientProfile>(`/admin/clients/${id}/suspend`, { reason }),
  restoreClient: (id: string) => api.post<ClientProfile>(`/admin/clients/${id}/restore`),
  approveClient: (id: string) => api.post<ClientProfile>(`/admin/clients/${id}/approve`),
  archiveClient: (id: string, reason: string) =>
    api.post<ClientProfile>(`/admin/clients/${id}/archive`, { reason }),
  clientHealth: (id: string) => api.get<ClientHealthProfile>(`/admin/clients/${id}/health`),
  recordHealthClearance: (
    id: string,
    payload: { status: string; notes: string | null },
  ) => api.post<ClientHealthProfile>(`/admin/clients/${id}/health/clearance`, payload),
  clientGoals: (id: string) => api.get<ClientGoal[]>(`/admin/clients/${id}/goals`),
  clientNotes: (id: string) => api.get<ClientNote[]>(`/admin/clients/${id}/notes`),
  addClientNote: (id: string, payload: { body: string; visibility: string }) =>
    api.post<ClientNote>(`/admin/clients/${id}/notes`, payload),
  clientConsents: (id: string) => api.get<ConsentRecord[]>(`/admin/clients/${id}/consents`),
  clientTimeline: (id: string, query?: { page?: number }) =>
    api.get<Paginated<ClientTimelineEntry>>(`/admin/clients/${id}/timeline`, { query }),
  exportClients: (query?: ClientDirectoryQuery) =>
    api.get<string>('/admin/clients/export', { query, responseType: 'text' }),

  coaches: () => api.get<CoachProfileSummary[]>('/admin/coaches'),
  locations: () => api.get<TrainingLocationDto[]>('/admin/locations'),

  exercises: (query?: ExerciseQuery) => api.get<ExercisePage>('/admin/exercises', { query }),
  exercise: (id: string) => api.get<ExerciseDto>(`/admin/exercises/${id}`),
  createExercise: (payload: UpsertExercisePayload) =>
    api.post<ExerciseDto>('/admin/exercises', payload),
  updateExercise: (id: string, payload: Partial<UpsertExercisePayload>) =>
    api.patch<ExerciseDto>(`/admin/exercises/${id}`, payload),
  duplicateExercise: (id: string) => api.post<ExerciseDto>(`/admin/exercises/${id}/duplicate`),
  archiveExercise: (id: string) => api.post<ExerciseDto>(`/admin/exercises/${id}/archive`),

  trainingTemplates: () => api.get<TrainingTemplateDto[]>('/admin/training-templates'),
  trainingPrograms: (query?: { page?: number; clientId?: string; status?: string[] }) =>
    api.get<Paginated<TrainingProgramDto>>('/admin/training-programs', { query }),
  trainingProgram: (id: string) => api.get<TrainingProgramDto>(`/admin/training-programs/${id}`),
  createTrainingProgram: (payload: CreateProgramPayload) =>
    api.post<TrainingProgramDto>('/admin/training-programs', payload),
  updateTrainingProgramDraft: (id: string, payload: UpdateProgramDraftPayload) =>
    api.patch<TrainingProgramDto>(`/admin/training-programs/${id}`, payload),
  publishTrainingProgram: (id: string) =>
    api.post<TrainingProgramDto>(`/admin/training-programs/${id}/publish`),
  assignTrainingProgram: (id: string, payload: AssignProgramPayload) =>
    api.post<TrainingProgramDto>(`/admin/training-programs/${id}/assign`, payload),
  duplicateTrainingProgram: (id: string, payload: { clientId: string | null; name: string }) =>
    api.post<TrainingProgramDto>(`/admin/training-programs/${id}/duplicate`, payload),
  archiveTrainingProgram: (id: string) =>
    api.post<TrainingProgramDto>(`/admin/training-programs/${id}/archive`),

  nutritionTemplates: () => api.get<NutritionTemplateDto[]>('/admin/nutrition-templates'),
  nutritionPlans: (query?: NutritionPlanQuery) =>
    api.get<NutritionPlanPage>('/admin/nutrition-plans', { query }),
  nutritionPlan: (id: string) => api.get<NutritionPlanDto>(`/admin/nutrition-plans/${id}`),
  createNutritionPlan: (payload: CreateNutritionPlanPayload) =>
    api.post<NutritionPlanDto>('/admin/nutrition-plans', payload),
  updateNutritionPlanDraft: (id: string, payload: UpdateNutritionDraftPayload) =>
    api.patch<NutritionPlanDto>(`/admin/nutrition-plans/${id}`, payload),
  publishNutritionPlan: (id: string) =>
    api.post<NutritionPlanDto>(`/admin/nutrition-plans/${id}/publish`),
  assignNutritionPlan: (id: string, payload: AssignNutritionPlanPayload) =>
    api.post<NutritionPlanDto>(`/admin/nutrition-plans/${id}/assign`, payload),
  archiveNutritionPlan: (id: string) =>
    api.post<NutritionPlanDto>(`/admin/nutrition-plans/${id}/archive`),
  estimateNutrition: (payload: NutritionEstimateRequest) =>
    api.post<NutritionEstimate>('/admin/nutrition-plans/estimate', payload),

  checkIns: (query?: CheckInQuery) => api.get<CheckInPage>('/admin/check-ins', { query }),
  checkIn: (id: string) => api.get<CheckInDto>(`/admin/check-ins/${id}`),
  checkInReviewContext: (id: string) =>
    api.get<CheckInReviewContext>(`/admin/check-ins/${id}/review-context`),
  reviewCheckIn: (id: string, payload: ReviewCheckInPayload) =>
    api.post<CheckInDto>(`/admin/check-ins/${id}/review`, payload),
  reopenCheckIn: (id: string, payload: ReopenCheckInPayload) =>
    api.post<CheckInDto>(`/admin/check-ins/${id}/reopen`, payload),
  requestCheckIn: (payload: RequestCheckInPayload) =>
    api.post<CheckInDto>('/admin/check-ins/request', payload),
  checkInTemplates: () => api.get<CheckInTemplateDto[]>('/admin/check-in-templates'),
  createCheckInTemplate: (payload: UpsertCheckInTemplatePayload) =>
    api.post<CheckInTemplateDto>('/admin/check-in-templates', payload),
  updateCheckInTemplate: (id: string, payload: Partial<UpsertCheckInTemplatePayload>) =>
    api.patch<CheckInTemplateDto>(`/admin/check-in-templates/${id}`, payload),

  formTemplates: () => api.get<import('@ircf/contracts').FormTemplateDto[]>('/admin/form-templates'),
  createFormTemplate: (payload: import('@ircf/contracts').UpsertFormTemplatePayload) =>
    api.post<import('@ircf/contracts').FormTemplateDto>('/admin/form-templates', payload),
  updateFormTemplate: (id: string, payload: Partial<import('@ircf/contracts').UpsertFormTemplatePayload>) =>
    api.patch<import('@ircf/contracts').FormTemplateDto>(`/admin/form-templates/${id}`, payload),
  formPacks: () => api.get<import('@ircf/contracts').FormPackDto[]>('/admin/form-packs'),
  createFormPack: (payload: import('@ircf/contracts').UpsertFormPackPayload) =>
    api.post<import('@ircf/contracts').FormPackDto>('/admin/form-packs', payload),
  updateFormPack: (id: string, payload: Partial<import('@ircf/contracts').UpsertFormPackPayload>) =>
    api.patch<import('@ircf/contracts').FormPackDto>(`/admin/form-packs/${id}`, payload),
  formAssignments: (query?: import('@ircf/contracts').FormAssignmentQuery) =>
    api.get<import('@ircf/contracts').FormAssignmentPage>('/admin/form-assignments', { query }),
  formAssignment: (id: string) =>
    api.get<import('@ircf/contracts').FormAssignmentDto>(`/admin/form-assignments/${id}`),
  issueForm: (payload: import('@ircf/contracts').IssueFormPayload) =>
    api.post<import('@ircf/contracts').FormAssignmentDto[]>('/admin/forms/issue', payload),
  reviewForm: (id: string, payload: import('@ircf/contracts').ReviewFormPayload) =>
    api.post<import('@ircf/contracts').FormAssignmentDto>(`/admin/form-assignments/${id}/review`, payload),

  feedback: (query?: FeedbackQuery) => api.get<FeedbackPage>('/admin/feedback', { query }),
  feedbackItem: (id: string) => api.get<FeedbackDto>(`/admin/feedback/${id}`),
  updateFeedback: (id: string, payload: UpdateFeedbackPayload) =>
    api.patch<FeedbackDto>(`/admin/feedback/${id}`, payload),

  availabilityRules: (coachId?: string) =>
    api.get<CoachAvailabilityRuleDto[]>('/admin/availability/rules', { query: { coachId } }),
  createAvailabilityRule: (payload: Omit<CoachAvailabilityRuleDto, 'id'>) =>
    api.post<CoachAvailabilityRuleDto>('/admin/availability/rules', payload),
  updateAvailabilityRule: (id: string, payload: Partial<CoachAvailabilityRuleDto>) =>
    api.patch<CoachAvailabilityRuleDto>(`/admin/availability/rules/${id}`, payload),
  deleteAvailabilityRule: (id: string) => api.delete<void>(`/admin/availability/rules/${id}`),
  availabilityExceptions: (coachId?: string) =>
    api.get<AvailabilityExceptionDto[]>('/admin/availability/exceptions', { query: { coachId } }),
  createAvailabilityException: (payload: Omit<AvailabilityExceptionDto, 'id' | 'createdAt'>) =>
    api.post<AvailabilityExceptionDto>('/admin/availability/exceptions', payload),
  deleteAvailabilityException: (id: string) =>
    api.delete<void>(`/admin/availability/exceptions/${id}`),

  sessionTypes: () => api.get<SessionTypeDto[]>('/admin/session-types'),
  createSessionType: (payload: Partial<SessionTypeDto>) =>
    api.post<SessionTypeDto>('/admin/session-types', payload),
  updateSessionType: (id: string, payload: Partial<SessionTypeDto>) =>
    api.patch<SessionTypeDto>(`/admin/session-types/${id}`, payload),

  packages: () => api.get<ServicePackageDto[]>('/admin/packages'),
  createPackage: (payload: UpsertServicePackagePayload) =>
    api.post<ServicePackageDto>('/admin/packages', payload),
  updatePackage: (id: string, payload: Partial<UpsertServicePackagePayload>) =>
    api.patch<ServicePackageDto>(`/admin/packages/${id}`, payload),
  clientPackages: (query?: { page?: number; clientId?: string; status?: string[] }) =>
    api.get<ClientPackagePage>('/admin/client-packages', { query }),
  createClientPackage: (payload: CreateClientPackagePayload) =>
    api.post<ClientPackageDto>('/admin/client-packages', payload),
  creditLedger: (query?: CreditLedgerQuery) =>
    api.get<CreditLedgerPage>('/admin/credits/ledger', { query }),
  creditBalance: (clientId: string) =>
    api.get<CreditBalance>('/admin/credits/balance', { query: { clientId } }),
  adjustCredits: (payload: AdjustCreditsPayload) =>
    api.post<CreditBalance>('/admin/credits/adjust', payload),
  recordManualPayment: (payload: RecordManualPaymentPayload) =>
    api.post<PaymentDto>('/admin/payments/manual', payload),

  conversations: (query?: ConversationQuery) =>
    api.get<ConversationPage>('/admin/conversations', { query }),
  messages: (conversationId: string, query?: { page?: number }) =>
    api.get<MessagePage>(`/admin/conversations/${conversationId}/messages`, { query }),
  sendMessage: (payload: SendMessagePayload) =>
    api.post<ConversationDto>('/admin/messages', payload),
  markConversationRead: (conversationId: string, upToMessageId: string) =>
    api.post<void>(`/admin/conversations/${conversationId}/read`, { upToMessageId }),

  documents: (query?: DocumentQuery) => api.get<DocumentPage>('/admin/documents', { query }),
  requestDocumentUpload: (payload: {
    clientId: string | null;
    fileName: string;
    contentType: string;
    contentLength: number;
  }) => api.post<UploadTicket>('/admin/documents/upload-url', payload),
  createDocument: (payload: UploadDocumentPayload) =>
    api.post<DocumentDto>('/admin/documents', payload),
  documentDownloadUrl: (id: string) =>
    api.post<SignedDownload>(`/admin/documents/${id}/download-url`),
  archiveDocument: (id: string) => api.post<DocumentDto>(`/admin/documents/${id}/archive`),

  contentEntries: () => api.get<import('@ircf/contracts').WebsiteContentEntryDto[]>('/admin/content'),
  contentReadiness: () => api.get<ContentReadinessReport>('/admin/content/readiness'),
  updateContent: (payload: UpdateContentPayload) => api.patch<void>('/admin/content', payload),
  publishContent: (payload: { keys: string[]; note: string | null }) =>
    api.post<void>('/admin/content/publish', payload),

  media: (query?: import('@ircf/contracts').MediaQuery) =>
    api.get<Paginated<FileAssetDto>>('/admin/media', { query }),
  requestMediaUpload: (payload: {
    category: string;
    fileName: string;
    contentType: string;
    contentLength: number;
    isPublic: boolean;
  }) => api.post<UploadTicket>('/admin/media/upload-url', payload),
  createMedia: (payload: {
    category: string;
    objectKey: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    isPublic: boolean;
    title: string | null;
    altText: string | null;
  }) => api.post<FileAssetDto>('/admin/media', payload),
  updateMedia: (id: string, payload: UpdateMediaPayload) =>
    api.patch<FileAssetDto>(`/admin/media/${id}`, payload),
  archiveMedia: (id: string) => api.post<FileAssetDto>(`/admin/media/${id}/archive`),
  modelAssets: () => api.get<ModelAssetContent[]>('/admin/media/model-assets'),
  updateModelAsset: (assetKey: string, payload: Partial<ModelAssetContent>) =>
    api.patch<ModelAssetContent>(`/admin/media/model-assets/${assetKey}`, payload),

  testimonials: (query?: { page?: number; status?: string[] }) =>
    api.get<TestimonialPage>('/admin/testimonials', { query }),
  createTestimonial: (payload: UpsertTestimonialPayload) =>
    api.post<import('@ircf/contracts').TestimonialDto>('/admin/testimonials', payload),
  updateTestimonial: (id: string, payload: Partial<UpsertTestimonialPayload>) =>
    api.patch<import('@ircf/contracts').TestimonialDto>(`/admin/testimonials/${id}`, payload),
  publishTestimonial: (id: string) =>
    api.post<import('@ircf/contracts').TestimonialDto>(`/admin/testimonials/${id}/publish`),
  unpublishTestimonial: (id: string, reason: string) =>
    api.post<import('@ircf/contracts').TestimonialDto>(`/admin/testimonials/${id}/unpublish`, { reason }),
  results: (query?: PublicResultQuery) => api.get<PublicResultPage>('/admin/results', { query }),
  createResult: (payload: UpsertPublicResultPayload) =>
    api.post<import('@ircf/contracts').PublicResultDto>('/admin/results', payload),
  updateResult: (id: string, payload: Partial<UpsertPublicResultPayload>) =>
    api.patch<import('@ircf/contracts').PublicResultDto>(`/admin/results/${id}`, payload),
  requestTestimonialConsent: (payload: {
    clientId: string;
    scope: Record<string, boolean>;
    message: string | null;
  }) => api.post<void>('/admin/testimonial-consents/request', payload),
  revokeTestimonialConsent: (id: string, reason: string) =>
    api.post<void>(`/admin/testimonial-consents/${id}/revoke`, {
      reason,
      unpublishDependentContent: true,
    }),

  leads: (query?: LeadQuery) => api.get<LeadPage>('/admin/leads', { query }),
  updateLead: (id: string, payload: UpdateLeadPayload) =>
    api.patch<import('@ircf/contracts').LeadDto>(`/admin/leads/${id}`, payload),
  convertLead: (id: string, payload: ConvertLeadPayload) =>
    api.post<ConvertLeadResponse>(`/admin/leads/${id}/convert`, payload),

  reportDefinitions: () => api.get<ReportDefinition[]>('/admin/reports'),
  report: (key: string, filters: ReportFilters) =>
    api.get<ReportResult>(`/admin/reports/${key}`, { query: filters }),
  reportCsv: (key: string, filters: ReportFilters) =>
    api.get<string>(`/admin/reports/${key}/export`, {
      query: filters,
      responseType: 'text',
    }),

  notificationDeliveries: (query?: NotificationDeliveryQuery) =>
    api.get<NotificationDeliveryPage>('/admin/notifications/deliveries', { query }),
  backgroundJobs: (query?: { page?: number; status?: string[] }) =>
    api.get<BackgroundJobPage>('/admin/notifications/jobs', { query }),
  retryJob: (id: string) => api.post<void>(`/admin/notifications/jobs/${id}/retry`),
  mailStatus: () => api.get<MailStatusDto>('/admin/notifications/mail-status'),
  mailbox: (query: {
    direction?: 'INBOUND' | 'OUTBOUND';
    page?: number;
    pageSize?: number;
    search?: string;
  }) =>
    api.get<MailboxPage>('/admin/notifications/mailbox', { query }),
  sendMailboxEmail: (payload: SendMailboxEmailPayload) =>
    api.post<MailboxMessageDto>('/admin/notifications/mailbox/send', payload),
  markMailboxRead: (id: string) =>
    api.patch<MailboxMessageDto>(`/admin/notifications/mailbox/${id}/read`),

  settings: () => api.get<AppSettingsDto>('/admin/settings'),
  updateSettings: (payload: UpdateAppSettingsPayload) =>
    api.patch<AppSettingsDto>('/admin/settings', payload),

  audit: (query?: AuditLogQuery) => api.get<AuditLogPage>('/admin/audit', { query }),
  exportAudit: (query?: AuditLogQuery) =>
    api.get<string>('/admin/audit/export', { query, responseType: 'text' }),

  externalCalendars: () =>
    api.get<ExternalCalendarConnectionDto[]>('/admin/external-calendars'),

  fitnessTests: (clientId: string) =>
    api.get<FitnessTestDto[]>('/admin/fitness-tests', { query: { clientId } }),
  recordFitnessTest: (clientId: string, payload: Record<string, unknown>) =>
    api.post<FitnessTestDto>('/admin/fitness-tests', { clientId, ...payload }),
  recordMeasurement: (clientId: string, payload: RecordMeasurementPayload) =>
    api.post<MeasurementDto>('/admin/measurements', { clientId, ...payload }),
  clientProgress: (clientId: string, query?: ProgressQuery) =>
    api.get<ProgressOverview>(`/admin/clients/${clientId}/progress`, { query }),
  clientAnalysis: (clientId: string, query?: ProgressQuery) =>
    api.get<ClientAnalysisOverview>(`/admin/clients/${clientId}/analysis`, { query }),
  clientProgressPhotos: (clientId: string, query?: { page?: number }) =>
    api.get<ProgressPhotoPage>(`/admin/clients/${clientId}/progress-photos`, { query }),
  clientProgressPhotoDownloadUrl: (photoId: string) =>
    api.post<SignedDownload>(`/admin/progress-photos/${photoId}/download-url`),
  clientWorkouts: (clientId: string, query?: WorkoutLogQuery) =>
    api.get<WorkoutLogPage>('/admin/workouts', { query: { ...query, clientId } }),
} as const;

export interface AdministratorAccount {
  id: string;
  email: string;
  displayName: string | null;
  role: 'client_admin' | 'system_admin';
  approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  disabled: boolean;
  emailVerified: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface InviteAdministratorPayload {
  email: string;
  displayName: string;
}
